<template>
  <div class="dashboard-page">
    <div class="main-content">
      <header class="content-header">
        <h1>Options - Aegis</h1>
        <p class="subtitle">
          Microsoft 365 Configuration Compliance Assessment
        </p>

        <div class="control-panel">
          <!-- Client Selection -->
          <div class="form-group">
            <label for="client-select">Select Client</label>
            <select id="client-select" v-model="selectedClient">
              <option value="">Choose a client...</option>
              <option v-for="client in clients" :key="client" :value="client">
                {{ client }}
              </option>
            </select>
          </div>

          <!-- Baseline Type Selection -->
          <div class="form-group">
            <label for="baseline-type">Baseline Type</label>
            <select id="baseline-type" v-model="comparisonType">
              <option value="standard">
                CIS Standard Baseline (70 checks)
              </option>
              <option value="options">Options Baseline</option>
            </select>
          </div>

          <!-- Single Run Button -->
          <button
            @click="runComparison"
            :disabled="!selectedClient || loading"
            class="btn-primary"
          >
            <span v-if="loading" class="spinner"></span>
            <span v-else>▶</span>
            {{ loading ? "Running..." : "Run Comparison" }}
          </button>
        </div>

        <!-- Info Banner -->
        <div class="info-banner">
          <span class="info-icon">ℹ️</span>
          <p>
            <strong>How it works:</strong> Select a client and baseline type,
            then click "Run Comparison" to analyse their M365 configuration
            against security best practices. Results will be organised by
            service area.
          </p>
        </div>
      </header>

      <div class="content">
        <LoadingBar
          :loading="loading"
          :progressPercentage="progressPercentage"
        />
        <div v-if="errorMessage" class="error">{{ errorMessage }}</div>

        <!-- Display Fetched Configurations -->
        <div
          v-if="
            showConfigurations &&
            Object.keys(baselineConfig).length &&
            Object.keys(clientConfig).length
          "
          class="config-container"
        >
          <div class="config">
            <h2>Baseline Configuration</h2>
            <pre>{{ formattedBaselineConfig }}</pre>
          </div>
          <div class="config">
            <h2>Client Configuration</h2>
            <pre>{{ formattedClientConfig }}</pre>
          </div>
        </div>

        <!-- Total Passes and Total Fails Summary Tiles -->
        <div
          v-if="!showConfigurations && Object.keys(filteredResults).length > 0"
          class="comparison-results"
        >
          <div class="comparison-header">
            <h2>Detailed Comparison Results</h2>
            <div class="toggle-container">
              <SwitchToggle
                v-model:checked="showAllResults"
                label="Show All Results"
              />
              <span class="toggle-label">{{ switchLabel }}</span>
              <!-- Export Report Button shown only when comparisonCompleted is true -->
              <button
                v-if="comparisonCompleted"
                @click="openExportModal"
                class="export-button"
              >
                Export Report
              </button>
            </div>
          </div>
          <StatusTiles :totalPasses="totalPasses" :totalFails="totalFails" />
        </div>

        <!-- Stats Tiles Summary -->
        <div
          v-if="Object.keys(filteredResults).length > 0"
          class="improved-stats-tiles"
        >
          <ImprovedStatsTile
            v-for="(categoryResults, categoryName) in filteredResults"
            :key="categoryName"
            :areaTitle="categoryName"
            :iconSrc="getCategoryIcon(categoryName)"
            :policyCounts="{
              totalPasses: categoryResults.totalPasses,
              totalFails: categoryResults.totalFails,
            }"
            :passingPolicies="categoryResults.passingPolicies"
            :failingPolicies="categoryResults.failingPolicies"
            @more-info="openPolicyModal"
          />
        </div>

        <!-- Search and Action Buttons -->
        <div v-if="comparisonCompleted" class="controls-row">
          <div class="search-filter-bar">
            <input
              v-model="categorySearchTerm"
              type="text"
              placeholder="Search categories..."
              class="category-search"
            />
          </div>
          <div class="action-buttons-row">
            <button @click="expandAllSections" class="btn-secondary">
              Expand All
            </button>
            <button @click="collapseAllSections" class="btn-secondary">
              Collapse All
            </button>
          </div>
        </div>

        <!-- Collapsible Category Sections -->
        <div v-if="comparisonCompleted" class="category-sections">
          <div
            v-for="[category, data] in filteredSections"
            :key="category"
            class="category-section-collapsible"
          >
            <!-- Category Header (Clickable) -->
            <button @click="toggleSection(category)" class="category-header">
              <div class="category-left">
                <span class="expand-icon">
                  {{ expandedSections[category] ? "▼" : "▶" }}
                </span>

                <img
                  v-if="getCategoryIcon(category)"
                  :src="getCategoryIcon(category)"
                  :alt="category"
                  class="category-icon"
                />

                <h2>{{ category }}</h2>
                <span class="policy-count"
                  >({{
                    Object.keys(data.policies || {}).length
                  }}
                  policies)</span
                >
              </div>

              <div class="category-stats">
                <span
                  v-if="data.totalPasses > 0"
                  class="stat-badge stat-success"
                >
                  ✓ {{ data.totalPasses }}
                </span>
                <span v-if="data.totalFails > 0" class="stat-badge stat-danger">
                  ✗ {{ data.totalFails }}
                </span>
              </div>
            </button>

            <!-- Expanded Content -->
            <div v-if="expandedSections[category]" class="category-content">
              <div
                v-if="Object.keys(data.policies || {}).length > 0"
                class="policy-list"
              >
                <div
                  v-for="(results, policyName) in data.policies"
                  :key="policyName"
                  class="policy-item"
                >
                  <div class="policy-header">
                    <h4>{{ policyName }}</h4>
                    <span
                      :class="[
                        'policy-status',
                        results.every((r) => r.Equal)
                          ? 'status-pass'
                          : 'status-fail',
                      ]"
                    >
                      {{ results.every((r) => r.Equal) ? "PASS" : "FAIL" }}
                    </span>
                  </div>

                  <!-- Policy Results Table -->
                  <table class="results-table">
                    <thead>
                      <tr>
                        <th>Setting</th>
                        <th>Expected Value</th>
                        <th>Current Value</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(result, index) in results" :key="index">
                        <td>{{ result.Path }}</td>
                        <td>{{ formatValue(result.RefValue) }}</td>
                        <td>{{ formatValue(result.DiffValue) }}</td>
                        <td
                          :class="result.Equal ? 'status-pass' : 'status-fail'"
                        >
                          {{ result.Equal ? "PASS" : "FAIL" }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-else class="empty-state">
                <span class="empty-icon">📄</span>
                <p>No policies to display in this category</p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="standardBaselineActive" class="baseline-note">
          <div class="info-banner">
            <i class="info-icon">ℹ️</i>
            <p>
              Currently showing comparison against standard security baseline.
              These are security best practices that should be present in all
              Microsoft 365 tenants.
            </p>
          </div>
        </div>
      </div>

      <!-- Policy Modal for Detailed Viewing -->
      <PolicyModal
        v-if="isPolicyModalOpen"
        :show="isPolicyModalOpen"
        :areaTitle="selectedAreaTitle"
        :passingPolicies="selectedPassingPolicies"
        :failingPolicies="selectedFailingPolicies"
        @close="closePolicyModal"
      />

      <!-- Export Report Modal -->
      <ExportReportModal
        v-if="isExportModalOpen"
        :show="isExportModalOpen"
        @close="closeExportModal"
        @export="handleExportReport"
      />
    </div>
  </div>
</template>

<script>
import ImprovedStatsTile from "@/views/components/ImprovedStatsTile.vue";
import LoadingBar from "@/views/components/LoadingBar.vue";
import { msalInstance } from "@/auth.js";
import {
  fetchClients,
  // cSpell:ignore fetchAllJsonFilesv3, fetchAndCompareAllFilesv6
  fetchAllJsonFilesv3,
  fetchJsonFile,
  fetchAndCompareAllFilesv6,
} from "@/azure-devops-api.js";
import { mapState } from "vuex";
import SwitchToggle from "@/views/components/SwitchToggle.vue";
import StatusTiles from "@/views/components/StatusTiles.vue";
import PolicyModal from "@/views/components/PolicyModal.vue";
import ExportReportModal from "@/views/components/ExportReportModal.vue";
import {
  conditionalAccessChecks,
  azureADDirectorySettingsChecks,
  deviceRegistrationPolicyChecks,
  defenderOffice365Checks,
  exchangeSecurityChecks,
} from "@/standard-security-baseline.js";

import { generateBaselineReport } from "@/utils/pdfGenerator.js";
import { categoryIcons as CATEGORY_ICONS } from "@/services/policy-utils.js";

export default {
  components: {
    LoadingBar,
    SwitchToggle,
    StatusTiles,
    ImprovedStatsTile,
    PolicyModal,
    ExportReportModal,
  },
  data() {
    return {
      categorySearchTerm: "",
      expandedSections: {},
      searchTerm: "",
      filterStatus: "all",
      comparisonType: "standard", //
      selectedClient: "",
      clients: [],
      baselineConfig: {},
      clientConfig: {},
      categorizedResults: {},
      showConfigurations: false,
      loading: false,
      progressPercentage: 0,
      errorMessage: "",
      showAllResults: true,
      isPolicyModalOpen: false,
      selectedAreaTitle: "",
      selectedPassingPolicies: [],
      selectedFailingPolicies: [],
      isExportModalOpen: false,
      logoFile: null,
      supportMessage: "",
      comparisonCompleted: false,
      exportAllResults: true,
      loggedInUser: null,
      standardBaselineActive: false,
    };
  },
  computed: {
    ...mapState(["isLoggedIn"]),
    formattedBaselineConfig() {
      return JSON.stringify(this.baselineConfig, null, 2);
    },
    formattedClientConfig() {
      return JSON.stringify(this.clientConfig, null, 2);
    },
    filteredResults() {
      return Object.entries(this.categorizedResults).reduce(
        (acc, [category, policies]) => {
          if (category.toLowerCase().includes("descriptions")) {
            console.log(`🛑 Removing Descriptions from categorizedResults`);
            return acc; // 🚀 Skip Descriptions category
          }

          const passingPolicies = [];
          const failingPolicies = [];

          const filteredPolicies = Object.entries(policies).reduce(
            (policyAcc, [policyName, results]) => {
              const filteredResults = this.showAllResults
                ? results
                : results.filter((result) => !result.Equal);

              if (filteredResults.length > 0) {
                policyAcc[policyName] = filteredResults;

                if (filteredResults.every((result) => result.Equal)) {
                  passingPolicies.push(policyName);
                } else {
                  failingPolicies.push(policyName);
                }
              }
              return policyAcc;
            },
            {}
          );

          if (Object.keys(filteredPolicies).length > 0) {
            acc[category] = {
              totalPasses: passingPolicies.length,
              totalFails: failingPolicies.length,
              policies: filteredPolicies,
              passingPolicies,
              failingPolicies,
            };
          }
          return acc;
        },
        {}
      );
    },

    categoryIcons() {
      return CATEGORY_ICONS;
    },

    switchLabel() {
      return this.showAllResults ? "Show All Results" : "Show Only Fails";
    },
    totalPasses() {
      return Object.values(this.categorizedResults)
        .flatMap((category) => Object.values(category))
        .flat()
        .filter((result) => result.Equal).length;
    },
    totalFails() {
      return Object.values(this.filteredResults) // ✅ Uses filteredResults instead
        .flatMap((category) => Object.values(category.policies || {}))
        .flat()
        .filter((result) => !result.Equal).length;
    },

    // Calculate total stats across all categories
    totalStats() {
      return Object.values(this.categorizedResults).reduce(
        (acc, category) => ({
          passed: acc.passed + (category.totalPasses || 0),
          failed: acc.failed + (category.totalFails || 0),
          total: acc.total + Object.keys(category.policies || {}).length,
        }),
        { passed: 0, failed: 0, total: 0 }
      );
    },

    // Calculate overall compliance score
    overallScore() {
      return this.totalStats.total > 0
        ? Math.round((this.totalStats.passed / this.totalStats.total) * 100)
        : 0;
    },

    // Filter sections based on status filter
    filteredSections() {
      let filtered = Object.entries(this.filteredResults);

      // Apply status filter
      filtered = filtered.filter(([, data]) => {
        if (this.filterStatus === "passed") return data.totalPasses > 0;
        if (this.filterStatus === "failed") return data.totalFails > 0;
        return true;
      });

      // Apply search filter
      if (this.categorySearchTerm) {
        filtered = filtered.filter(([category]) =>
          category.toLowerCase().includes(this.categorySearchTerm.toLowerCase())
        );
      }

      return filtered;
    },
  },
  async created() {
    this.loading = true;
    try {
      const accessToken = await this.acquireToken();
      this.clients = await fetchClients(
        accessToken,
        "62b297c6-760c-4250-a926-52b8850e6d5f",
        "/Clients"
      );
    } catch (error) {
      this.errorMessage = "Error fetching clients: " + error.message;
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async acquireToken() {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length) {
        const accessTokenRequest = {
          account: accounts[0],
          scopes: ["499b84ac-1321-427f-aa17-267ca6975798/user_impersonation"],
        };
        const response = await msalInstance.acquireTokenSilent(
          accessTokenRequest
        );

        // Retrieve and store the logged-in user's details
        this.loggedInUser = {
          upn: accounts[0].username,
          displayName: accounts[0].name || accounts[0].username,
        };

        return response.accessToken;
      } else {
        throw new Error("No accounts available for authentication.");
      }
    },

    async loadCategoryIconsAsBase64() {
      const icons = {};
      const iconMap = CATEGORY_ICONS;

      for (const [category, iconFile] of Object.entries(iconMap)) {
        try {
          const iconPath = require(`@/assets/${iconFile}`);
          // Convert to base64
          const response = await fetch(iconPath);
          const blob = await response.blob();
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          icons[category] = base64;
        } catch (e) {
          console.warn(`Could not load icon for ${category}`);
        }
      }
      return icons;
    },
    async fetchClientConfig() {
      this.showConfigurations = true;
      this.comparisonResults = [];
      this.categorizedResults = {};
      this.loading = true;
      this.progressPercentage = 0;
      this.errorMessage = "";

      try {
        const accessToken = await this.acquireToken();
        const baselineDir = "/M365Config/Resources";
        const clientDir = `/Clients/${this.selectedClient}/M365Config/Resources`;

        // Progress callback for listing files
        const updateProgressListing = (progress) => {
          this.progressPercentage = progress * 0.5;
        };

        // Define excluded folders for consistency
        const excludedFolders = [
          "/M365Config/Resources/Descriptions",
          "/M365Config/Resources/Office365",
          "/M365Config/Resources/PowerPlatform",
          "/M365Config/Resources/SecurityCompliance",
        ];

        console.log("🚀 Setting Excluded Folders:", excludedFolders);

        // Fetch files using fetchAllJsonFilesv3 with the exclusions
        const [baselineFiles, clientFiles] = await Promise.all([
          fetchAllJsonFilesv3(
            accessToken,
            "62b297c6-760c-4250-a926-52b8850e6d5f",
            baselineDir,
            excludedFolders,
            updateProgressListing
          ),
          fetchAllJsonFilesv3(
            accessToken,
            "62b297c6-760c-4250-a926-52b8850e6d5f",
            clientDir,
            excludedFolders,
            updateProgressListing
          ),
        ]);

        this.baselineConfig = {};
        this.clientConfig = {};

        const totalFiles = baselineFiles.length + clientFiles.length;
        let processedFiles = 0;

        // Update progress for content fetching
        const updateProgressContent = () => {
          processedFiles += 1;
          const contentProgress = Math.floor(
            (processedFiles / totalFiles) * 50
          );
          this.progressPercentage = 50 + contentProgress;
        };

        // Process baseline files
        const baselineFilePromises = baselineFiles.map(async (file) => {
          const response = await fetchJsonFile(
            accessToken,
            "62b297c6-760c-4250-a926-52b8850e6d5f",
            file
          );
          updateProgressContent();
          try {
            return {
              relativePath: file.replace(baselineDir, ""),
              content: JSON.parse(response.content),
            };
          } catch (error) {
            console.error(`Invalid JSON in file: ${file}`, error);
            return {
              relativePath: file.replace(baselineDir, ""),
              content: null,
            };
          }
        });

        // Process client files
        const clientFilePromises = clientFiles.map(async (file) => {
          const response = await fetchJsonFile(
            accessToken,
            "62b297c6-760c-4250-a926-52b8850e6d5f",
            file
          );
          updateProgressContent();
          try {
            return {
              relativePath: file.replace(clientDir, ""),
              content: JSON.parse(response.content),
            };
          } catch (error) {
            console.error(`Invalid JSON in file: ${file}`, error);
            return {
              relativePath: file.replace(clientDir, ""),
              content: null,
            };
          }
        });

        const baselineFileContents = await Promise.all(baselineFilePromises);
        const clientFileContents = await Promise.all(clientFilePromises);

        baselineFileContents.forEach((file) => {
          this.baselineConfig[file.relativePath] = file.content;
        });
        clientFileContents.forEach((file) => {
          this.clientConfig[file.relativePath] = file.content;
        });

        this.showConfigurations = true;
      } catch (error) {
        this.errorMessage =
          "Error fetching client configuration: " + error.message;
      } finally {
        this.loading = false;
        this.progressPercentage = 0;
      }
    },
    async compareStandardBaseline() {
      this.loading = true;
      this.errorMessage = "";
      this.showConfigurations = false;
      this.comparisonCompleted = false;
      this.standardBaselineActive = true;

      try {
        // Fetch client configuration first
        const accessToken = await this.acquireToken();
        const clientDir = `/Clients/${this.selectedClient}/M365Config/Resources`;

        const excludedFolders = [
          "/M365Config/Resources/Descriptions",
          "/M365Config/Resources/Office365",
          "/M365Config/Resources/PowerPlatform",
          "/M365Config/Resources/SecurityCompliance",
        ];

        console.log(
          "Fetching client configuration for standard baseline comparison..."
        );

        // Fetch client files
        const clientFiles = await fetchAllJsonFilesv3(
          accessToken,
          "62b297c6-760c-4250-a926-52b8850e6d5f",
          clientDir,
          excludedFolders,
          (progress) => {
            this.progressPercentage = Math.floor(progress * 30); // 0-30% for fetching
          }
        );

        // Load client config
        this.clientConfig = {};
        const clientFilePromises = clientFiles.map(async (file) => {
          const response = await fetchJsonFile(
            accessToken,
            "62b297c6-760c-4250-a926-52b8850e6d5f",
            file
          );
          try {
            return {
              relativePath: file.replace(clientDir, ""),
              content: JSON.parse(response.content),
            };
          } catch (error) {
            console.error(`Invalid JSON in file: ${file}`, error);
            return {
              relativePath: file.replace(clientDir, ""),
              content: null,
            };
          }
        });

        const clientFileContents = await Promise.all(clientFilePromises);
        clientFileContents.forEach((file) => {
          this.clientConfig[file.relativePath] = file.content;
        });

        console.log("Client configuration loaded. Starting baseline checks...");
        this.progressPercentage = 35;

        // Initialize results structure with categories
        this.categorizedResults = {
          "Conditional Access": {},
          "Azure AD Settings": {},
          "Device Settings": {},
          "Defender for Office 365": {},
          "Exchange Security": {},
        };

        // 1. Check Conditional Access policies
        this.progressPercentage = 40;
        const conditionalAccessPathPattern =
          "/azuread/conditionalaccess/conditionalaccesspolicies/";
        const conditionalAccessPaths = Object.keys(this.clientConfig).filter(
          (path) => path.toLowerCase().includes(conditionalAccessPathPattern)
        );

        console.log(
          "Matched conditional access paths:",
          conditionalAccessPaths
        );

        if (conditionalAccessPaths.length > 0) {
          const clientConditionalAccessPolicies = [];

          for (const path of conditionalAccessPaths) {
            const policyContent = this.clientConfig[path];

            if (
              policyContent &&
              typeof policyContent === "object" &&
              !Array.isArray(policyContent)
            ) {
              clientConditionalAccessPolicies.push(policyContent);
            } else if (Array.isArray(policyContent)) {
              clientConditionalAccessPolicies.push(...policyContent);
            }
          }

          console.log(
            "Extracted conditional access policies:",
            clientConditionalAccessPolicies
          );

          if (clientConditionalAccessPolicies.length > 0) {
            for (const [checkId, check] of Object.entries(
              conditionalAccessChecks
            )) {
              const result = check.checkFunction(
                clientConditionalAccessPolicies
              );

              this.categorizedResults["Conditional Access"][check.title] = [
                {
                  Path: checkId,
                  RefValue: "Compliant",
                  DiffValue: result ? "Compliant" : "Non-compliant",
                  Equal: result,
                  Description: check.description,
                },
              ];
            }
          }
        }

        // 2. Check Azure AD Directory Settings
        this.progressPercentage = 55;
        const directorySettingsPattern = "/azuread/azureaddirectorysetting";
        const directorySettingsPaths = Object.keys(this.clientConfig).filter(
          (path) => path.toLowerCase().includes(directorySettingsPattern)
        );

        if (directorySettingsPaths.length > 0) {
          const directorySettings = [];

          for (const path of directorySettingsPaths) {
            const settingContent = this.clientConfig[path];
            if (settingContent && typeof settingContent === "object") {
              directorySettings.push(settingContent);
            }
          }

          const authPolicyPattern = "/azuread/authorization policy";
          const authPolicyPaths = Object.keys(this.clientConfig).filter(
            (path) => path.toLowerCase().includes(authPolicyPattern)
          );

          if (authPolicyPaths.length > 0) {
            for (const path of authPolicyPaths) {
              const policyContent = this.clientConfig[path];
              if (policyContent && typeof policyContent === "object") {
                directorySettings.push(policyContent);
              }
            }
          }

          if (directorySettings.length > 0) {
            for (const [checkId, check] of Object.entries(
              azureADDirectorySettingsChecks
            )) {
              const result = check.checkFunction(directorySettings);
              this.categorizedResults["Azure AD Settings"][check.title] = [
                {
                  Path: checkId,
                  RefValue: "Compliant",
                  DiffValue: result ? "Compliant" : "Non-compliant",
                  Equal: result,
                  Description: check.description,
                },
              ];
            }
          }
        }

        // 3. Check Device Registration Policy
        this.progressPercentage = 70;
        const devicePolicyPattern =
          "/azuread/devicesettings/deviceregistrationpolicy";
        const devicePolicyPaths = Object.keys(this.clientConfig).filter(
          (path) => path.toLowerCase().includes(devicePolicyPattern)
        );

        if (devicePolicyPaths.length > 0) {
          const devicePolicy = this.clientConfig[devicePolicyPaths[0]];

          if (devicePolicy && typeof devicePolicy === "object") {
            for (const [checkId, check] of Object.entries(
              deviceRegistrationPolicyChecks
            )) {
              const result = check.checkFunction(devicePolicy);
              this.categorizedResults["Device Settings"][check.title] = [
                {
                  Path: checkId,
                  RefValue: "Compliant",
                  DiffValue: result ? "Compliant" : "Non-compliant",
                  Equal: result,
                  Description: check.description,
                },
              ];
            }
          }
        }

        // 4. Check Defender for Office 365 Settings
        this.progressPercentage = 85;
        const defenderO365Pattern = "/defender/office_365/default";
        const defenderO365Paths = Object.keys(this.clientConfig).filter(
          (path) => path.toLowerCase().includes(defenderO365Pattern)
        );

        if (defenderO365Paths.length > 0) {
          const defenderPolicy = this.clientConfig[defenderO365Paths[0]];

          if (defenderPolicy && typeof defenderPolicy === "object") {
            for (const [checkId, check] of Object.entries(
              defenderOffice365Checks
            )) {
              const result = check.checkFunction(defenderPolicy);
              this.categorizedResults["Defender for Office 365"][check.title] =
                [
                  {
                    Path: checkId,
                    RefValue: "Compliant",
                    DiffValue: result ? "Compliant" : "Non-compliant",
                    Equal: result,
                    Description: check.description,
                  },
                ];
            }
          }
        }

        // 5. Check Exchange Security Settings
        this.progressPercentage = 95;
        const exchangePattern = "/exchange/";
        const exchangePaths = Object.keys(this.clientConfig).filter((path) =>
          path.toLowerCase().includes(exchangePattern)
        );

        if (exchangePaths.length > 0) {
          const exchangeSettings = [];

          for (const path of exchangePaths) {
            const settingContent = this.clientConfig[path];
            if (settingContent && typeof settingContent === "object") {
              exchangeSettings.push(settingContent);
            }
          }

          if (exchangeSettings.length > 0) {
            for (const [checkId, check] of Object.entries(
              exchangeSecurityChecks
            )) {
              const result = check.checkFunction(exchangeSettings);
              this.categorizedResults["Exchange Security"][check.title] = [
                {
                  Path: checkId,
                  RefValue: "Compliant",
                  DiffValue: result ? "Compliant" : "Non-compliant",
                  Equal: result,
                  Description: check.description,
                },
              ];
            }
          }
        }

        // Check if we have any results at all
        const hasResults = Object.values(this.categorizedResults).some(
          (category) => Object.keys(category).length > 0
        );

        if (!hasResults) {
          this.errorMessage =
            "No security settings found to evaluate in the client configuration.";
          this.loading = false;
          return;
        }

        this.progressPercentage = 100;
        this.comparisonCompleted = true;
      } catch (error) {
        this.errorMessage =
          "Error comparing to standard baseline: " + error.message;
        console.error("Standard baseline comparison error:", error);
      } finally {
        this.loading = false;
        this.progressPercentage = 0;
      }
    },

    getCategoryIcon(category) {
      const iconName = this.categoryIcons[category];
      if (iconName) {
        try {
          return require(`@/assets/${iconName}`);
        } catch (e) {
          console.warn(`Icon not found for category: ${category}`);
          return null;
        }
      }
      return null;
    },

    formatValue(value) {
      if (value === null || value === undefined) return "N/A";
      if (typeof value === "object") return JSON.stringify(value, null, 2);
      if (typeof value === "boolean") return value ? "Enabled" : "Disabled";
      return String(value);
    },
    // Toggle section expansion
    toggleSection(section) {
      this.expandedSections = {
        ...this.expandedSections,
        [section]: !this.expandedSections[section],
      };
    },

    // Expand all sections after comparison completes
    expandAllSections() {
      const expanded = {};
      Object.keys(this.categorizedResults).forEach((key) => {
        expanded[key] = true;
      });
      this.expandedSections = expanded;
    },

    // Collapse all sections
    collapseAllSections() {
      this.expandedSections = {};
    },

    // Unified comparison method - replaces separate buttons
    async runComparison() {
      // Determine which baseline to use based on selection
      if (this.comparisonType === "standard") {
        await this.compareStandardBaseline();
      } else {
        await this.compareFiles();
      }
      // Auto-expand all sections after comparison
      //this.expandAllSections();
    },

    async compareFiles() {
      this.loading = true;
      this.errorMessage = "";
      this.showConfigurations = false;
      this.comparisonCompleted = false;
      this.standardBaselineActive = false;

      try {
        const accessToken = await this.acquireToken();
        const baselineDir = "/M365Config/Resources";
        const clientDir = `/Clients/${this.selectedClient}/M365Config/Resources`;

        const updateProgress = (progress) => {
          this.progressPercentage = progress;
        };

        // Define the same excluded folders for comparison
        const excludedFolders = [
          "/M365Config/Resources/Descriptions",
          "/M365Config/Resources/Office365",
          "/M365Config/Resources/PowerPlatform",
          "/M365Config/Resources/SecurityCompliance",
        ];

        this.categorizedResults = await fetchAndCompareAllFilesv6(
          accessToken,
          "62b297c6-760c-4250-a926-52b8850e6d5f",
          baselineDir,
          clientDir,
          [
            "DefaultMailboxRegion",
            "AllowedMailboxRegions",
            "RejectAnonymousDirectSend",
            "RPSEnabled",
            "appStoreUrl",
            "DefaultAuthenticationPolicy",
            "ReleaseTrack",
            "Identity",
            "Name",
            "InPlaceHolds",
            "deviceNameTemplate",
            "Size",
            "largeIcon.value",
            "deployedAppCount",
            "ImmutableId",
            "Priority",
            "CreatedBy",
            "LastModifiedBy",
            "Guid",
            "OrganizationId",
            "OrganizationalUnitRoot",
            "ObjectCategory",
            "DistinguishedName",
            "UnifiedAuditLogFirstOptInDate",
            "@odata.type",
            "id",
            "securityIdentifier",
            "authenticationStrength@odata.context",
            "grantControls.authenticationStrength@odata.context",
            "advancedThreatProtectionOnboardingBlob",
            "description",
            "version",
            "createdDateTime",
            "lastModifiedDateTime",
            "settings@odata.context",
            "informationUrl",
            "ExchangeObjectId",
            "GroupLifeCyclePolicy",
            "GroupsCreationWhitelistedId",
            "AdminDisplayVersion",
            "RBACConfigurationVersion",
            "properties.metadata.createdBy",
            "properties.metadata.createdOn",
            "properties.metadata.updatedBy",
            "properties.metadata.updatedOn",
            "systemData.createdBy",
            "systemData.createdAt",
            "systemData.lastModifiedBy",
            "systemData.lastModifiedAt",
            "conditions.users.excludeUsers",
            "conditions.locations.excludeLocations",
            "conditions.locations.includeLocations",
            "MicrosoftExchangeRecipientEmailAddresses",
            "MicrosoftExchangeRecipientPrimarySmtpAddress",
            "SharePointUrl",
            "LegacyExchangeDN",
            "CurrentMailboxForests",
            "isAssigned",
            "mailNickname",
            "onPremisesObjectIdentifier",
            "wellKnownObject",
            "appMetadata",
          ],
          updateProgress,
          excludedFolders
        );

        console.log("Categorized Results:", this.categorizedResults);
        this.showConfigurations = false;
        this.comparisonCompleted = true;
      } catch (error) {
        this.errorMessage = "Error comparing files: " + error.message;
      } finally {
        this.loading = false;
        this.progressPercentage = 0;
      }
    },
    async exportToPDF({ supportMessage, logoFile, exportAllResults }) {
      // Load category icons
      const categoryIcons = await this.loadCategoryIconsAsBase64();

      // Determine which results to export
      const resultsToExport = exportAllResults
        ? this.categorizedResults
        : this.filteredResultsOnlyFails();

      // Determine baseline type
      const baselineType = this.standardBaselineActive
        ? "Standard Security Baseline"
        : "Options IT Baseline";

      // Call the new pdfmake generator
      generateBaselineReport({
        clientName: this.selectedClient,
        categorizedResults: resultsToExport,
        exportAllResults: exportAllResults,
        baselineType: baselineType,
        user: this.loggedInUser,
        logoFile: logoFile,
        supportMessage: supportMessage,
        categoryIcons: categoryIcons,
      });
    },
    generatePDFContent(doc, startY, pageHeight, resultsToExport) {
      let yPosition = startY;

      Object.entries(resultsToExport).forEach(([category, policies]) => {
        if (yPosition + 10 > pageHeight) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(12);
        doc.text(category, 10, yPosition);
        yPosition += 10;

        Object.entries(policies).forEach(([policyName, results]) => {
          if (yPosition + 20 > pageHeight) {
            doc.addPage();
            yPosition = 20;
          }

          doc.setFontSize(10);
          doc.text(policyName, 10, yPosition);
          yPosition += 10;

          const rows = results.map((result) => [
            result.Path,
            result.RefValue,
            result.DiffValue,
            result.Equal ? "Pass" : "Fail",
          ]);

          doc.autoTable({
            head: [["Setting", "Desired State", "Client Tenant", "Status"]],
            body: rows,
            startY: yPosition,
            theme: "grid",
            headStyles: {
              fillColor: [224, 224, 224],
              textColor: [51, 51, 51],
              fontSize: 10,
              halign: "left",
            },
            bodyStyles: {
              textColor: [51, 51, 51],
              cellPadding: 5,
              fontSize: 9,
              overflow: "linebreak",
            },
            alternateRowStyles: {
              fillColor: [249, 249, 249],
            },
            margin: { left: 10, right: 10 },
            styles: {
              fontSize: 8,
              cellPadding: 3,
              tableWidth: "auto",
            },
            columnStyles: {
              0: { cellWidth: 55 },
              1: { cellWidth: 55 },
              2: { cellWidth: 55 },
              3: { cellWidth: 20, halign: "center" },
            },
            didParseCell: (data) => {
              if (data.column.index === 3) {
                if (data.cell.raw === "Pass") {
                  data.cell.styles.fillColor = [56, 179, 95];
                  data.cell.styles.textColor = [255, 255, 255];
                } else if (data.cell.raw === "Fail") {
                  data.cell.styles.fillColor = [231, 76, 60];
                  data.cell.styles.textColor = [255, 255, 255];
                }
              }
            },
          });

          yPosition = doc.lastAutoTable.finalY + 10;
        });
      });
    },

    filteredResultsOnlyFails() {
      return Object.entries(this.categorizedResults).reduce(
        (acc, [category, policies]) => {
          const failedPolicies = Object.entries(policies).reduce(
            (policyAcc, [policyName, results]) => {
              const failedResults = results.filter((result) => !result.Equal);
              if (failedResults.length) policyAcc[policyName] = failedResults;
              return policyAcc;
            },
            {}
          );

          if (Object.keys(failedPolicies).length)
            acc[category] = failedPolicies;
          return acc;
        },
        {}
      );
    },

    openExportModal() {
      if (
        !this.categorizedResults ||
        !Object.keys(this.categorizedResults).length
      ) {
        this.errorMessage =
          "No comparison results found. Please compare files before exporting.";
        console.warn(this.errorMessage);
        return;
      }
      this.isExportModalOpen = true;
    },

    closeExportModal() {
      this.isExportModalOpen = false;
      this.errorMessage = "";
    },

    async handleExportReport({ logoFile, supportMessage, exportAllResults }) {
      this.logoFile = logoFile;
      this.supportMessage = supportMessage;
      this.exportAllResults = exportAllResults;
      await this.exportToPDF({ supportMessage, logoFile, exportAllResults });
      this.closeExportModal();
    },

    openPolicyModal({ areaTitle, passingPolicies, failingPolicies }) {
      this.selectedAreaTitle = areaTitle;
      this.selectedPassingPolicies = passingPolicies || [];
      this.selectedFailingPolicies = failingPolicies || [];
      this.isPolicyModalOpen = true;
    },

    closePolicyModal() {
      this.isPolicyModalOpen = false;
      this.selectedAreaTitle = "";
      this.selectedPassingPolicies = [];
      this.selectedFailingPolicies = [];
    },
  },
};
</script>

<style scoped>
/* Modern CSS Reset and Base Styles */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Typography */
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

body {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  line-height: 1.6;
  background-color: #f8fafc;
  color: #1e293b;
  margin: 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Main Dashboard Page */
.dashboard-page {
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Main Content Area */
.main-content {
  background-color: #ffffff;
  min-height: 100vh;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

/* Content Header */
.content-header {
  background-color: #ffffff;
  padding: 2rem;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.content-header h1 {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
}

/* Dropdown Section */
.dropdown {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.dropdown label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
}

.dropdown select {
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s;
  min-width: 200px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.25rem;
  padding-right: 2.5rem;
}

.dropdown select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Buttons */
.dropdown button,
.export-button {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

/* Button Variants */
button {
  background-color: #3b82f6;
  color: white;
}

button:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

button:active {
  transform: translateY(0);
}

button:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

.btn-compare-standard {
  background-color: #10b981;
}

.btn-compare-standard:hover {
  background-color: #059669;
}

/* Content Area */
.content {
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
  background-color: #f8fafc;
}

/* Configuration Container - Updated for better responsiveness */
.config-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
  max-width: 100%;
  overflow-x: auto;
}

/* For larger screens, show side by side */
@media (min-width: 1024px) {
  .config-container {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1.5rem;
  }
}

.config {
  background-color: #ffffff;
  padding: 1.5rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  min-width: 0; /* Prevent grid blowout */
  overflow: hidden;
}

.config h2 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
}

.config pre {
  background-color: #f8fafc;
  color: #1e293b;
  padding: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  font-family: "Monaco", "Menlo", monospace;
  font-size: 0.8125rem;
  max-height: 400px;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all; /* Ensure long strings break */
  max-width: 100%;
}

/* Comparison Results Header */
.comparison-results {
  margin-bottom: 2rem;
}

.comparison-header {
  background-color: #ffffff;
  padding: 1.5rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

.comparison-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.toggle-label {
  font-size: 0.875rem;
  color: #475569;
  white-space: nowrap;
}

/* Stats Tiles */
.stats-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

/* Info Banner */
.baseline-note {
  margin-bottom: 1.5rem;
}

.info-banner {
  display: flex;
  align-items: flex-start;
  background-color: #dbeafe;
  border: 1px solid #3b82f6;
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  gap: 0.75rem;
}

.info-icon {
  font-size: 1.375rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

.info-banner {
  display: flex;
  align-items: flex-start;
  background: linear-gradient(to right, #dbeafe, #e0f2fe);
  border: 1px solid #93c5fd;
  border-radius: 0.625rem;
  padding: 1rem 1.25rem;
  gap: 0.875rem;
  margin-top: 1.5rem;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.08);
}

.info-banner p {
  color: #1e40af;
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0;
}

.info-banner strong {
  color: #1e3a8a;
  font-weight: 600;
}

/* Category and Policy Sections */
.category-section {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Improved Stats Tiles Grid */
.improved-stats-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
  margin-bottom: 2rem;
}

.category-section h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.policy-section {
  margin-bottom: 1.5rem;
}

.policy-section:last-child {
  margin-bottom: 0;
}

.policy-section h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.75rem;
}

/* Tables */
table {
  width: 100%;
  border-collapse: collapse;
  background-color: #ffffff;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  table-layout: fixed;
}

table th {
  background-color: #f8fafc;
  color: #64748b;
  font-weight: 600;
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
}

table td {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.875rem;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
}

table tr:hover {
  background-color: #f8fafc;
}

table tr:last-child td {
  border-bottom: none;
}

/* Column Widths */
th:nth-of-type(1),
td:nth-of-type(1) {
  width: 30%;
}

th:nth-of-type(2),
td:nth-of-type(2) {
  width: 25%;
}

th:nth-of-type(3),
td:nth-of-type(3) {
  width: 25%;
}

th:nth-of-type(4),
td:nth-of-type(4) {
  width: 20%;
  text-align: center;
}

/* Status Cells */
td.status-pass {
  background-color: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  font-weight: 600;
  text-align: center;
  border-radius: 0.25rem;
}

td.status-fail {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  font-weight: 600;
  text-align: center;
  border-radius: 0.25rem;
}

/* Loading State */
.loading {
  text-align: center;
  font-size: 1rem;
  color: #64748b;
  padding: 3rem;
}

/* Error State */
.error {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  padding: 1rem;
  border-radius: 0.375rem;
  text-align: center;
  margin-bottom: 1rem;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

/* Export Button */
.export-button {
  background-color: #8b5cf6;
  color: white;
  white-space: nowrap;
}

.export-button:hover {
  background-color: #7c3aed;
}

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .config-container {
    grid-template-columns: 1fr;
  }

  .stats-tiles {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .content-header {
    padding: 1.5rem;
  }

  .content {
    padding: 1.5rem;
  }

  .dropdown {
    flex-direction: column;
    align-items: stretch;
  }

  .dropdown select {
    width: 100%;
  }

  .dropdown button {
    width: 100%;
    justify-content: center;
  }

  .comparison-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .toggle-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    width: 100%;
  }

  table {
    font-size: 0.75rem;
  }

  table th,
  table td {
    padding: 0.5rem;
  }

  .control-panel {
    flex-direction: column;
    align-items: stretch;
  }

  .form-group {
    width: 100%;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }
}

/* Animation for content loading */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.config,
.category-section,
.comparison-header {
  animation: fadeIn 0.3s ease-out;
}

/* Utility classes for component integration */
.mb-4 {
  margin-bottom: 1rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.mt-4 {
  margin-top: 1rem;
}

/* Control Panel */
.control-panel {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.form-group {
  flex: 1;
  min-width: 220px;
}

.form-group label {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.form-group select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s ease;
  min-width: 220px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23475569' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.75rem center;
  background-repeat: no-repeat;
  background-size: 1.25rem;
  padding-right: 2.75rem;
  cursor: pointer;
  font-weight: 500;
}

.form-group select:hover {
  border-color: #cbd5e1;
  background-color: #f8fafc;
}

.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  background-color: #ffffff;
}

.subtitle {
  color: #64748b;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
}

.stat-success .stat-value {
  color: #16a34a;
}
.stat-danger .stat-value {
  color: #ef4444;
}
.stat-warning .stat-value {
  color: #f59e0b;
}

/* Filters Bar */
.filters-bar {
  background: white;
  border-radius: 0.75rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  border: 1px solid #e2e8f0;
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
}

.filter-btn {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.filter-passed.active {
  background: #16a34a;
  border-color: #16a34a;
}

.filter-failed.active {
  background: #ef4444;
  border-color: #ef4444;
}

/* Category Sections */
.category-sections {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.category-section {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.category-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.category-header:hover {
  background: #f8fafc;
}

.category-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}

.category-left {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.expand-icon {
  font-size: 1rem;
  color: #64748b;
}

.category-header h2 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.policy-count {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 400;
}

.category-stats {
  display: flex;
  gap: 0.75rem;
}

.stat-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
}

.stat-badge.stat-success {
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
}

.stat-badge.stat-danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

/* Category Content */
.category-content {
  padding: 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.policy-item {
  background: white;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #e2e8f0;
}

.policy-item:last-child {
  margin-bottom: 0;
}

.policy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.policy-header h4 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.policy-status {
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.policy-status.status-pass {
  background: #dcfce7;
  color: #16a34a;
}

.policy-status.status-fail {
  background: #fee2e2;
  color: #ef4444;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #64748b;
}

.empty-icon {
  font-size: 3rem;
  display: block;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.action-buttons-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  justify-content: flex-end;
}

.btn-secondary {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background-color: #f8fafc;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: #e2e8f0;
}

.btn-primary {
  padding: 0.875rem 2rem;
  font-size: 0.9375rem;
  font-weight: 600;
  border: none;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3),
    0 2px 4px -1px rgba(59, 130, 246, 0.2);
  white-space: nowrap;
  min-height: 44px;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.35),
    0 4px 6px -2px rgba(59, 130, 246, 0.25);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px -1px rgba(59, 130, 246, 0.3);
}

.btn-primary:disabled {
  background: linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%);
  cursor: not-allowed;
  opacity: 0.7;
  transform: none;
  box-shadow: none;
}

.btn-primary .spinner {
  animation: spin 1s linear infinite;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.controls-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.search-filter-bar {
  flex: 1;
  min-width: 250px;
}

.category-search {
  width: 100%;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s ease;
}

.category-search:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.category-search::placeholder {
  color: #94a3b8;
}
</style>
